import {
	Button,
	type ColumnDef,
	DataTable,
	FilterBuilder,
	type FilterField,
	TintedBackgroundIcon,
	useFilterBuilder,
	useUrlState,
} from "@monbits/ui";
import {
	AlertCircle,
	AlertTriangle,
	Clock,
	Folder,
	RefreshCcw,
} from "lucide-react";
import * as React from "react";
import { Outlet, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { StatusBadge } from "src/components/status-badge";
import alertsQueries from "src/queries/alerts-queries";
import type { AlertSeverity, AlertStatus, SystemAlert } from "src/types";
import {
	ALERT_CATEGORY_CONFIG,
	getAlertCategoryConfig,
} from "src/utils/entity-config";
import { getSeverityConfig, getStatusConfig } from "src/utils/status-config";

// Alert statuses to show in filter
const ALERT_STATUSES = ["open", "acknowledged", "resolved"] as const;

// Generate status options from unified config
const STATUS_OPTIONS = ALERT_STATUSES.map((status) => {
	const config = getStatusConfig(status);
	return {
		value: status,
		label: config.label,
		icon: (
			<TintedBackgroundIcon icon={config.icon} color={config.color} size="sm" />
		),
	};
});

// Severity levels to show in filter
const SEVERITY_LEVELS = ["info", "warn", "error", "critical"] as const;

// Generate severity options from unified config
const SEVERITY_OPTIONS = SEVERITY_LEVELS.map((severity) => {
	const config = getSeverityConfig(severity);
	return {
		value: severity,
		label: config.label,
		icon: (
			<TintedBackgroundIcon icon={config.icon} color={config.color} size="sm" />
		),
	};
});

// Alert categories for filter
const ALERT_CATEGORIES = Object.keys(ALERT_CATEGORY_CONFIG);

// Generate category options from config
const CATEGORY_OPTIONS = ALERT_CATEGORIES.map((category) => {
	const config = getAlertCategoryConfig(category);
	return {
		value: category,
		label: config.label,
		icon: (
			<TintedBackgroundIcon icon={config.icon} color={config.color} size="sm" />
		),
	};
});

const FILTER_FIELDS: FilterField[] = [
	{
		id: "status",
		label: "Status",
		type: "select",
		icon: <Clock className="h-4 w-4" />,
		options: STATUS_OPTIONS,
	},
	{
		id: "severity",
		label: "Severity",
		type: "select",
		icon: <AlertTriangle className="h-4 w-4" />,
		options: SEVERITY_OPTIONS,
	},
	{
		id: "category",
		label: "Category",
		type: "select",
		icon: <Folder className="h-4 w-4" />,
		options: CATEGORY_OPTIONS,
	},
];

const formatDateTime = (value?: string) => {
	if (!value) return "—";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return date.toLocaleString();
};

const columns: ColumnDef<SystemAlert>[] = [
	{
		id: "status",
		header: "Status",
		cell: (alert) => {
			const config = getStatusConfig(alert.status);
			return (
				<StatusBadge type={config.badgeType} icon={config.icon}>
					{config.label}
				</StatusBadge>
			);
		},
	},
	{
		id: "severity",
		header: "Severity",
		cell: (alert) => {
			const config = getSeverityConfig(alert.severity);
			return (
				<StatusBadge type={config.badgeType} icon={config.icon}>
					{config.label}
				</StatusBadge>
			);
		},
	},
	{
		id: "code",
		header: "Code",
		cell: (alert) => <span className="font-mono text-sm">{alert.code}</span>,
	},
	{
		id: "category",
		header: "Category",
		cell: (alert) => {
			if (!alert.category)
				return <span className="text-muted-foreground">—</span>;
			const config = getAlertCategoryConfig(alert.category);
			return (
				<div className="flex items-center gap-2">
					<TintedBackgroundIcon
						icon={config.icon}
						color={config.color}
						size="sm"
					/>
					<span className="font-medium">{config.label}</span>
				</div>
			);
		},
	},
	{
		id: "occurrences",
		header: "Count",
		className: "text-right",
		cell: (alert) => (
			<span className="font-mono tabular-nums">{alert.occurrenceCount}</span>
		),
	},
	{
		id: "lastTriggered",
		header: "Last Triggered",
		cell: (alert) => (
			<span className="text-muted-foreground">
				{formatDateTime(alert.lastTriggeredAt)}
			</span>
		),
	},
];

export function AlertsList() {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	const { filters, setFilters } = useFilterBuilder({
		fields: FILTER_FIELDS,
		searchParams,
		setSearchParams,
	});

	const [pagination, setPagination] = useUrlState({
		page: { type: "number", default: 1 },
		limit: { type: "number", default: 20 },
	});

	const offset = (pagination.page - 1) * pagination.limit;

	// Convert filter builder filters to API params
	const apiFilters = React.useMemo(() => {
		const result: {
			status?: AlertStatus;
			severity?: AlertSeverity;
			category?: string;
		} = {};

		for (const f of filters) {
			if (f.operator === "is") {
				if (f.field === "status") {
					result.status = f.value as AlertStatus;
				} else if (f.field === "severity") {
					result.severity = f.value as AlertSeverity;
				} else if (f.field === "category") {
					result.category = f.value as string;
				}
			}
		}

		return result;
	}, [filters]);

	const { data, isLoading, isFetching, refetch } = alertsQueries.listAlerts({
		...apiFilters,
		limit: pagination.limit,
		offset,
	});

	const alerts = data?.alerts ?? [];
	const totalCount = data?.count ?? 0;

	const handleRefresh = React.useCallback(() => {
		refetch();
		toast.success("Alerts refreshed");
	}, [refetch]);

	const handleRowClick = React.useCallback(
		(alert: SystemAlert) => {
			navigate(`/alerts/${alert.id}`);
		},
		[navigate],
	);

	return (
		<>
			<div className="space-y-6 p-4 sm:p-6">
				<div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
					<div>
						<h1 className="text-3xl font-bold">System Alerts</h1>
						<p className="text-sm text-muted-foreground mt-1">
							Monitor and manage system alerts across your platform.
							{totalCount > 0 &&
								` ${totalCount} alert${totalCount !== 1 ? "s" : ""} found.`}
						</p>
					</div>
					<Button
						variant="secondary"
						onClick={handleRefresh}
						disabled={isFetching}
					>
						<RefreshCcw className="mr-2 h-4 w-4" />
						Refresh
					</Button>
				</div>

				<FilterBuilder
					fields={FILTER_FIELDS}
					filters={filters}
					onFiltersChange={setFilters}
				/>

				<DataTable
					columns={columns}
					data={alerts}
					isLoading={isLoading}
					onRowClick={handleRowClick}
					getRowId={(row) => String(row.id)}
					emptyMessage={
						filters.length > 0 ? "No alerts match filters" : "No system alerts"
					}
					emptyDescription={
						filters.length > 0
							? "Try adjusting your filters to find what you're looking for."
							: "There are currently no system alerts to display."
					}
					emptyIcon={<AlertCircle className="h-12 w-12" />}
					pagination={
						totalCount > 0
							? {
									page: pagination.page,
									pageSize: pagination.limit,
									totalCount,
									onPageChange: (page: number) => setPagination({ page }),
									onPageSizeChange: (limit: number) =>
										setPagination({ limit, page: 1 }),
								}
							: undefined
					}
				/>
			</div>

			<Outlet context={{ alerts, isLoading }} />
		</>
	);
}

export type AlertsOutletContext = {
	alerts: SystemAlert[];
	isLoading: boolean;
};
